import { SupportedLanguage } from "../../../services/realtimeVoice";

export type PersonaType = 'listener' | 'active_advisor' | 'cheerleader' | 'sleep_companion';

interface HintEntry {
    en: string;
    es: string;
    keywords: string[];
}

const FALLBACK_DATA: Record<PersonaType, HintEntry[]> = {
    listener: [
        { en: "Tell me more.", es: "Cuéntame más.", keywords: ["more", "tell", "mas"] },
        { en: "Keep going.", es: "Sigue.", keywords: ["keep", "going", "sigue"] },
        { en: "Can you stay with me while I talk?", es: "¿Puedes quedarte conmigo mientras hablo?", keywords: ["stay", "talk", "quedarte"] },
        { en: "Do you understand what I mean?", es: "¿Entiendes lo que quiero decir?", keywords: ["understand", "mean", "entiendes"] },
        { en: "Help me put this into words.", es: "Ayúdame a ponerlo en palabras.", keywords: ["words", "say", "palabras"] },
        { en: "Ask me a question to help me talk.", es: "Hazme una pregunta para ayudarme a hablar.", keywords: ["question", "ask", "pregunta"] },
        { en: "What’s a good place to start?", es: "¿Por dónde es mejor empezar?", keywords: ["start", "begin", "empezar"] },
        { en: "I don’t know how to say this.", es: "No sé cómo decir esto.", keywords: ["don't know", "how", "no se"] },
        { en: "I just need someone to be here.", es: "Solo necesito que alguien esté aquí.", keywords: ["need", "here", "alguien"] },
        { en: "Stay with me while I talk.", es: "Quédate conmigo mientras hablo.", keywords: ["stay", "talk", "hablo"] }
    ],
    active_advisor: [ // active_advisor
        { en: "Help me make sense of this.", es: "Ayúdame a darle sentido a esto.", keywords: ["sense", "logic", "sentido"] },
        { en: "I’m stuck—what should I do?", es: "Estoy bloqueado/a—¿qué debería hacer?", keywords: ["stuck", "do", "bloqueado"] },
        { en: "What do you think is really going on here?", es: "¿Qué crees que realmente está pasando aquí?", keywords: ["going on", "happen", "pasando"] },
        { en: "Can you help me see it from another angle?", es: "¿Puedes ayudarme a verlo desde otro ángulo?", keywords: ["angle", "other", "angulo"] },
        { en: "What’s the most important part of this?", es: "¿Qué es lo más importante de esto?", keywords: ["important", "part", "importante"] },
        { en: "What are my realistic options?", es: "¿Cuáles son mis opciones reales?", keywords: ["options", "real", "opciones"] },
        { en: "If you were me, what would you do first?", es: "Si fueras yo, ¿qué harías primero?", keywords: ["first", "you", "primero"] },
        { en: "What’s the next step I should take?", es: "¿Cuál debería ser mi siguiente paso?", keywords: ["next", "step", "paso"] },
        { en: "What should I prioritize right now?", es: "¿Qué debería priorizar ahora mismo?", keywords: ["prioritize", "now", "priorizar"] },
        { en: "What’s the simplest plan from here?", es: "¿Cuál es el plan más simple a partir de aquí?", keywords: ["simple", "plan", "simple"] }
    ],
    cheerleader: [ // cheerleader
        { en: "I need encouragement.", es: "Necesito ánimo.", keywords: ["encouragement", "need", "animo"] },
        { en: "Can you be on my side?", es: "¿Puedes estar de mi lado?", keywords: ["side", "with", "lado"] },
        { en: "Tell me I’m going to be okay.", es: "Dime que voy a estar bien.", keywords: ["okay", "well", "bien"] },
        { en: "Can you comfort me?", es: "¿Puedes consolarme?", keywords: ["comfort", "help", "consolarme"] },
        { en: "I’m feeling really low.", es: "Me siento muy decaído/a.", keywords: ["low", "sad", "decaido"] },
        { en: "Remind me of what I did right.", es: "Recuérdame qué hice bien.", keywords: ["right", "good", "bien"] },
        { en: "Help me feel confident again.", es: "Ayúdame a recuperar la confianza.", keywords: ["confident", "trust", "confianza"] },
        { en: "I need reassurance.", es: "Necesito tranquilidad.", keywords: ["reassurance", "sure", "tranquilidad"] },
        { en: "Say something kind to me.", es: "Dime algo amable.", keywords: ["kind", "nice", "amable"] },
        { en: "Give me one small step I can do now.", es: "Dame un paso pequeño que pueda hacer ahora.", keywords: ["small", "now", "pequeño"] }
    ],
    sleep_companion: [ // sleep_companion
        { en: "Help me fall asleep.", es: "Ayúdame a dormir.", keywords: ["sleep", "asleep", "dormir"] },
        { en: "Help me relax.", es: "Ayúdame a relajarme.", keywords: ["relax", "clam", "relajarme"] },
        { en: "My mind won’t stop.", es: "Mi mente no se detiene.", keywords: ["mind", "stop", "mente"] },
        { en: "Guide my breathing.", es: "Guía mi respiración.", keywords: ["breathing", "breath", "respiracion"] },
        { en: "Slow it down with me.", es: "Baja el ritmo conmigo.,", keywords: ["slow", "down", "ritmo"] },
        { en: "Talk to me gently.", es: "Háblame con suavidad.", keywords: ["gently", "soft", "suavidad"] },
        { en: "Stay with me until I’m sleepy.", es: "Quédate conmigo hasta que me dé sueño.", keywords: ["stay", "sleepy", "sueño"] },
        { en: "Help me let today go.", es: "Ayúdame a soltar el día de hoy.", keywords: ["today", "let go", "soltar"] },
        { en: "I want calm, not analysis.", es: "Quiero calma, no análisis.", keywords: ["calm", "analysis", "calma"] },
        { en: "Give me something light to focus on.", es: "Dame algo ligero en lo que enfocarme.", keywords: ["focus", "light", "enfocarme"] }
    ]
};

/**
 * Returns 3 fallback hints based on persona, language, and last turn context.
 * Implements keyword matching and falls back to randomization for variety.
 */
export const getFallbackHints = (
    personaId: string, 
    language: SupportedLanguage, 
    lastTurnText?: string
): string[] => {
    const pId = (personaId || 'listener') as PersonaType;
    const langKey = language.startsWith('es') ? 'es' : 'en';
    const allHints = FALLBACK_DATA[pId] || FALLBACK_DATA['listener'];

    const searchStr = (lastTurnText || "").toLowerCase();
    
    // 1. Identify hits
    const hits = allHints.filter(h => 
        h.keywords.some(k => searchStr.includes(k.toLowerCase()))
    );

    // 2. Identify non-hits
    const nonHits = allHints.filter(h => !hits.includes(h));

    // 3. Shuffle non-hits for variety
    const shuffledNonHits = [...nonHits].sort(() => Math.random() - 0.5);

    // 4. Combine: Hits first, then shuffled non-hits
    const result = [...hits, ...shuffledNonHits].slice(0, 3);

    return result.map(h => h[langKey as 'en' | 'es']);
};
